import { useState, useRef, useEffect } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Dialog } from '@chakra-ui/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';

interface SearchResult {
  symbol: string;
  name: string;
  type: string;
  exchange: string;
  currency: string;
}

interface AddInstrumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
  isAtCap?: boolean;
}

export function AddInstrumentDialog({ open, onOpenChange, onAdded, isAtCap }: AddInstrumentDialogProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  function handleQueryChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.get<{ instruments: SearchResult[] }>(`/api/instruments/search?q=${encodeURIComponent(value)}`);
        setResults(data.instruments);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }

  async function handleAdd(instrument: SearchResult) {
    setAdding(instrument.symbol);
    try {
      await api.post('/api/watchlist', {
        symbol: instrument.symbol,
        name: instrument.name,
        instrumentType: instrument.type,
        exchange: instrument.exchange,
      });
      onAdded();
      onOpenChange(false);
    } catch {
      // Could already be in watchlist
    } finally {
      setAdding(null);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Dialog.Backdrop bg="bg.overlay" position="fixed" inset="0" zIndex="50" />
      <Dialog.Positioner>
        <Dialog.Content
          w="90vw"
          maxW="480px"
          maxH="70vh"
          bg="bg.primary"
          borderRadius="lg"
          border="1px solid"
          borderColor="border.primary"
          boxShadow="lg"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Box p="4" pb="0">
            <Dialog.Title
              fontSize="18px"
              fontWeight="500"
              lineHeight="28px"
              color="text.primary"
              mb="3"
            >
              Add instrument
            </Dialog.Title>
            <chakra.input
              ref={inputRef}
              type="text"
              placeholder="Search by symbol or name..."
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQueryChange(e.target.value)}
              w="full"
              px="4"
              py="3"
              borderRadius="md"
              border="1px solid"
              borderColor="border.primary"
              bg="bg.secondary"
              fontSize={{ base: '16px', md: '14px' }}
              color="text.primary"
              outline="none"
            />
          </Box>

          <Box flex="1" overflow="auto" p="3" px="4" pb="4">
            {searching && (
              <Text fontSize="14px" color="text.tertiary" py="4" textAlign="center">
                Searching...
              </Text>
            )}

            {!searching && query.length >= 2 && results.length === 0 && (
              <Text fontSize="14px" color="text.tertiary" py="4" textAlign="center">
                No instruments found
              </Text>
            )}

            {!searching && query.length < 2 && (
              <Text fontSize="14px" color="text.tertiary" py="4" textAlign="center">
                Type at least 2 characters to search
              </Text>
            )}

            {isAtCap && (
              <Text fontSize="14px" color="status.error.text" py="4" textAlign="center">
                Watchlist full. Remove an instrument to make space.
              </Text>
            )}

            {results.map((r) => (
              <chakra.button
                key={`${r.symbol}-${r.exchange}`}
                onClick={() => handleAdd(r)}
                disabled={adding === r.symbol || isAtCap}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                w="full"
                p="3"
                borderRadius="md"
                cursor="pointer"
                textAlign="left"
                bg="transparent"
                border="none"
                transition="background 150ms ease"
                _hover={{ bg: 'bg.active' }}
              >
                <Box>
                  <Flex align="center" gap="2">
                    <Text fontSize="14px" fontWeight="600" color="text.primary">{r.symbol}</Text>
                    <Box
                      as="span"
                      fontSize="12px"
                      color="text.tertiary"
                      px="6px"
                      py="1px"
                      borderRadius="sm"
                      bg="bg.tertiary"
                    >
                      {r.exchange}
                    </Box>
                  </Flex>
                  <Text fontSize="14px" color="text.secondary" mt="2px">{r.name}</Text>
                </Box>
                {adding === r.symbol ? (
                  <Text fontSize="14px" color="text.tertiary">Adding...</Text>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary, #667085)" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 5V19M5 12H19" />
                  </svg>
                )}
              </chakra.button>
            ))}
          </Box>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
